// Floating widget that flips the signed-in user's role between
// field_staff and admin. Renders only when the running Encore
// environment is in `ROLE_SWITCHER_ALLOWED_ENVS` — `local` (encore
// run) or `dev` (cloud dev env). Staging and production never match
// and never render the widget.
//
// Gating is runtime, driven by `user.environmentName` on the /me
// response, so a single Vite production bundle behaves correctly
// across dev / staging / production deploys without per-env build
// flags. The JSX still ships in the production bundle (a small cost)
// — defence in depth lives at the backend, where PUT
// /auth/dev/me/role refuses outside the same allow-list.

import { useState, type ReactElement } from 'react'

import { apiFetch, ApiError } from '../hooks/api'
import type { UserRole } from '@citycare/shared'

import { useAuth } from './auth-provider'

interface DevRoleResponse {
  userId: string
  role: UserRole
}

// Must match `DEV_ROLE_ALLOWED_ENVS` on the backend
// (apps/backend/src/auth/dev-role.ts). A drift between the two would
// surface as a button that 403s on click.
const ROLE_SWITCHER_ALLOWED_ENVS: ReadonlySet<string> = new Set([
  'local',
  'dev',
])

export function DevRoleSwitcher(): ReactElement | null {
  return <DevRoleSwitcherInner />
}

function DevRoleSwitcherInner(): ReactElement | null {
  const { user, refresh } = useAuth()
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!user) return null
  if (
    user.environmentName === undefined ||
    !ROLE_SWITCHER_ALLOWED_ENVS.has(user.environmentName)
  ) {
    return null
  }

  const otherRole: UserRole = user.role === 'admin' ? 'field_staff' : 'admin'

  const handleFlip = async () => {
    setPending(true)
    setError(null)
    try {
      await apiFetch<DevRoleResponse>('/auth/dev/me/role', {
        method: 'PUT',
        json: { role: otherRole },
      })
      refresh()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err))
    } finally {
      setPending(false)
    }
  }

  return (
    <div
      data-testid="dev-role-switcher"
      className="fixed bottom-4 right-4 z-[60] flex flex-col gap-1 rounded-lg border-2 border-dashed border-amber-500 bg-amber-50/95 p-3 text-xs shadow-lg"
    >
      <div className="font-mono font-semibold text-amber-900">
        DEV: {user.role}
      </div>
      <button
        type="button"
        onClick={() => void handleFlip()}
        disabled={pending}
        className="inline-flex min-h-9 items-center justify-center rounded-md bg-amber-700 px-3 py-1 font-medium text-white hover:bg-amber-800 disabled:opacity-50"
      >
        {pending ? 'Flipping…' : `→ ${otherRole}`}
      </button>
      {error !== null ? (
        <div className="max-w-[14rem] text-amber-900">{error}</div>
      ) : null}
    </div>
  )
}
